import { useCallback } from 'react';
import { Formik, Form, Field, type FormikHelpers } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { CContainer, CRow, CCol, CCardGroup, CCard, CCardBody } from '@coreui/react';
import OtpInput from 'components/inputs/OtpInput';
import * as yup from 'yup';
import { nullTypeError } from 'utils/yup';
import get from 'lodash/get';
import { useLocation, useHistory } from 'react-router-dom';
import TextInput from 'components/inputs/TextInput';
import { toast } from 'react-toastify';
import useLogin from 'hooks/auth/useLogin';
import useAuthFormSubmit from 'hooks/auth/useAuthFormSubmit';
import routePaths from 'router/route-paths';
import ErrorMessage from 'components/common/ErrorMessage';
import Button from 'components/common/Button';
import { messageOf } from 'utils/error';

interface ILoginTwoFAValues {
  otp: string;
  email: string | null;
  plainPassword: string | null;
  /** No field: the form error the submit sets with setErrors({ form }). */
  form?: string;
}

/** The location state the login navigates here with (useAuthFormSubmit). */
interface ILoginTwoFALocationState {
  user?: { email?: string; plainPassword?: string };
}

const useOnSubmitHandler = () => {
  const login = useLogin();
  const handleSubmit = useAuthFormSubmit(login);
  const history = useHistory();

  const onSubmit = useCallback(
    async (values: ILoginTwoFAValues, formikActions: FormikHelpers<ILoginTwoFAValues>) => {
      try {
        const data = await handleSubmit({ ...values, isAdmin: true }, formikActions);
        if (data) history.push(routePaths.default);
      } catch (error) {
        toast.error(messageOf(error));
      }
    },
    [handleSubmit, history],
  );
  return onSubmit;
};

const schema = yup.object().shape({
  email: yup
    .string()
    .required(() => <FormattedMessage id="errors.email_required" />)
    .email(() => <FormattedMessage id="errors.email_invalid" />)
    .nonNullable(nullTypeError('string')),
  plainPassword: yup
    .string()
    .required(() => <FormattedMessage id="errors.password_required" />)
    .nonNullable(nullTypeError('string')),
  otp: yup
    .string()
    .required(() => <FormattedMessage id="errors.fill_field" />)
    .typeError(() => <FormattedMessage id="errors.fill_field" />)
    // yup 0.28 rejected null with the type error (yup 1: with the required message).
    .nonNullable(() => <FormattedMessage id="errors.fill_field" />),
});

const LoginWithTwoFA = () => {
  const onSubmit = useOnSubmitHandler();
  const location = useLocation<ILoginTwoFALocationState | undefined>();
  const history = useHistory();
  const { formatMessage: __ } = useIntl();
  const email = get(location, 'state.user.email', null);
  const plainPassword = get(location, 'state.user.plainPassword', null);

  if (!email || !plainPassword) {
    history.replace(routePaths.login);
  }

  const onBack = useCallback(() => history.replace(routePaths.login), [history]);

  const initialValues: ILoginTwoFAValues = {
    otp: '',
    email,
    plainPassword,
  };

  return (
    <div className="auth-layout">
      <CContainer>
        <CRow className="justify-content-center align-items-center">
          <CCol md={10} lg={8} xl={6}>
            <CCardGroup>
              <CCard className="p-4">
                <CCardBody>
                  <Formik
                    initialValues={initialValues}
                    validationSchema={schema}
                    onSubmit={onSubmit}
                  >
                    {({ isSubmitting, errors }) => (
                      <>
                        <Form>
                          <h1>{__({ id: 'page_titles.login2FA' })}</h1>
                          <Field
                            disabled
                            component={TextInput}
                            type="email"
                            icon="cil-user"
                            name="email"
                            label={__({ id: 'auth.email' })}
                          />
                          <Field
                            component={TextInput}
                            disabled
                            type="password"
                            icon="cil-lock-locked"
                            name="plainPassword"
                            label={__({ id: 'auth.password' })}
                          />
                          <Field
                            name="otp"
                            component={OtpInput}
                            numInputs={6}
                            separator="-"
                            required
                            label={<FormattedMessage id="auth.verification_code_tip" />}
                          />
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginTop: '20px',
                            }}
                          >
                            <Button onClick={onBack} disabled={isSubmitting} color="secondary">
                              {__({ id: 'buttons.back' })}
                            </Button>
                            <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
                              {__({ id: 'buttons.submit' })}
                            </Button>
                          </div>
                        </Form>
                        <ErrorMessage message={errors.form} />
                      </>
                    )}
                  </Formik>
                </CCardBody>
              </CCard>
            </CCardGroup>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default LoginWithTwoFA;
